import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';

import { TableModule, Table } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { FileUploadModule } from 'primeng/fileupload';
import { ConfirmationService, MessageService } from 'primeng/api';

import { HeaderService } from '../../../services/header.service';
import { UserMlpApiService, ImportCsvResult } from '../../../services/user-mlp-api.service';
import { ShortenAddressPipe } from '../../../shared/pipes/shorten-address.pipe';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, skip } from 'rxjs';
import type { UserMlpResponseDto, CreateUserMlpDto, UpdateUserMlpDto } from '../../../api/models';

@Component({
  selector: 'app-user-mlp',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    IconFieldModule,
    InputIconModule,
    ConfirmDialogModule,
    MessageModule,
    TooltipModule,
    FileUploadModule,
    ShortenAddressPipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './user-mlp.component.html',
  styleUrl: './user-mlp.component.css',
})
export class UserMlpComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: Table;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('csvFileInput') csvFileInput!: ElementRef<HTMLInputElement>;

  private readonly fb = inject(FormBuilder);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);
  private readonly userMlpApi = inject(UserMlpApiService);

  protected readonly userMlpList = signal<UserMlpResponseDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly dialogVisible = signal(false);
  protected readonly dialogMode = signal<'create' | 'edit'>('create');
  protected readonly searchControl = new FormControl('');
  protected readonly importLoading = signal(false);

  protected userMlpForm!: FormGroup;
  private editingUserMlp: UserMlpResponseDto | null = null;
  private readonly destroy$ = new Subject<void>();
  private isSearching = false;

  ngOnInit(): void {
    this.headerService.setTitle(this.translateService.instant('userMlp.title'));
    this.headerService.setSubtitle(this.translateService.instant('userMlp.subtitle'));

    this.initForm();
    this.loadUserMlpList();

    // Setup server-side search with debounce
    this.searchControl.valueChanges
      .pipe(skip(1), takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.isSearching = true;
        this.loadUserMlpList(searchTerm || undefined);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.userMlpForm = this.fb.group({
      walletAddress: ['', [Validators.required]],
      mlpAmount: [0, [Validators.required, Validators.min(0)]],
    });
  }

  protected async loadUserMlpList(search?: string): Promise<void> {
    this.loading.set(true);
    this.searchControl.disable();

    try {
      const response = await this.userMlpApi.listUserMlp({ search });
      this.userMlpList.set(response.data);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('userMlp.loadError'),
      });
    } finally {
      this.loading.set(false);
      this.searchControl.enable({ emitEvent: false });

      if (this.isSearching && this.searchInput) {
        setTimeout(() => {
          this.searchInput.nativeElement.focus();
          this.isSearching = false;
        }, 0);
      }
    }
  }

  protected openCreateDialog(): void {
    this.dialogMode.set('create');
    this.editingUserMlp = null;
    this.userMlpForm.reset({
      walletAddress: '',
      mlpAmount: 0,
    });
    this.dialogVisible.set(true);
  }

  protected openEditDialog(userMlp: UserMlpResponseDto): void {
    this.dialogMode.set('edit');
    this.editingUserMlp = userMlp;
    this.userMlpForm.patchValue({
      walletAddress: userMlp.walletAddress,
      mlpAmount: userMlp.mlpAmount,
    });
    this.dialogVisible.set(true);
  }

  protected saveUserMlp(): void {
    if (this.userMlpForm.invalid) {
      Object.keys(this.userMlpForm.controls).forEach((key) => {
        this.userMlpForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading.set(true);

    if (this.dialogMode() === 'create') {
      this.createUserMlp();
    } else {
      this.updateUserMlp();
    }
  }

  private async createUserMlp(): Promise<void> {
    const dto: CreateUserMlpDto = {
      walletAddress: this.userMlpForm.value.walletAddress,
      mlpAmount: this.userMlpForm.value.mlpAmount,
    };

    try {
      const newUserMlp = await this.userMlpApi.createUserMlp(dto);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.success'),
        detail: this.translateService.instant('userMlp.createSuccess'),
      });
      this.dialogVisible.set(false);

      // Add to list
      const current = this.userMlpList();
      this.userMlpList.set([...current, newUserMlp]);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('userMlp.createError'),
      });
    } finally {
      this.loading.set(false);
    }
  }

  private async updateUserMlp(): Promise<void> {
    if (!this.editingUserMlp) return;

    const dto: UpdateUserMlpDto = {
      walletAddress: this.userMlpForm.value.walletAddress,
      mlpAmount: this.userMlpForm.value.mlpAmount,
    };

    try {
      const updated = await this.userMlpApi.updateUserMlp(this.editingUserMlp.id, dto);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.success'),
        detail: this.translateService.instant('userMlp.updateSuccess'),
      });
      this.dialogVisible.set(false);

      // Update local list
      const current = this.userMlpList();
      const next = current.map((item) => (item.id === updated.id ? updated : item));
      this.userMlpList.set(next);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('userMlp.updateError'),
      });
    } finally {
      this.loading.set(false);
    }
  }

  protected deleteUserMlp(userMlp: UserMlpResponseDto): void {
    const shortenedAddress = this.shortenAddress(userMlp.walletAddress);
    this.confirmationService.confirm({
      message: this.translateService.instant('userMlp.deleteConfirmMessage', {
        address: shortenedAddress,
      }),
      header: this.translateService.instant('userMlp.deleteConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.loading.set(true);

        try {
          await this.userMlpApi.deleteUserMlp(userMlp.id);

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.success'),
            detail: this.translateService.instant('userMlp.deleteSuccess'),
          });

          // Update the local list
          const current = this.userMlpList();
          this.userMlpList.set(current.filter((item) => item.id !== userMlp.id));
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('userMlp.deleteError'),
          });
        } finally {
          this.loading.set(false);
        }
      },
    });
  }

  protected openCsvImport(): void {
    this.csvFileInput.nativeElement.click();
  }

  protected onCsvFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    // Validate file type
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('userMlp.invalidFileType'),
      });
      this.resetFileInput();
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('userMlp.fileTooLarge'),
      });
      this.resetFileInput();
      return;
    }

    this.uploadCsvFile(file);
  }

  private async uploadCsvFile(file: File): Promise<void> {
    this.importLoading.set(true);

    try {
      const result = await this.userMlpApi.importCsv(file);

      // Show success message with details
      const detail =
        result.errors.length > 0
          ? this.translateService.instant('userMlp.importPartialSuccess', {
              created: result.created,
              updated: result.updated,
              errors: result.errors.length,
            })
          : this.translateService.instant('userMlp.importSuccess', {
              created: result.created,
              updated: result.updated,
            });

      this.messageService.add({
        severity: result.errors.length > 0 ? 'warn' : 'success',
        summary: this.translateService.instant('common.success'),
        detail,
      });

      // Reload list after import
      await this.loadUserMlpList();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('userMlp.importError'),
      });
    } finally {
      this.importLoading.set(false);
      this.resetFileInput();
    }
  }

  private resetFileInput(): void {
    if (this.csvFileInput) {
      this.csvFileInput.nativeElement.value = '';
    }
  }

  protected getDialogTitle(): string {
    return this.dialogMode() === 'create'
      ? this.translateService.instant('userMlp.createTitle')
      : this.translateService.instant('userMlp.editTitle');
  }

  protected isFieldInvalid(fieldName: string): boolean {
    const field = this.userMlpForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  protected getFieldError(fieldName: string): string {
    const field = this.userMlpForm.get(fieldName);
    if (!field || !field.errors || !field.touched) return '';

    if (field.errors['required']) {
      return this.translateService.instant('validation.required');
    }
    if (field.errors['pattern']) {
      return this.translateService.instant('userMlp.invalidWalletAddress');
    }
    if (field.errors['min']) {
      return this.translateService.instant('userMlp.minValueError', {
        min: field.errors['min'].min,
      });
    }
    if (field.errors['max']) {
      return this.translateService.instant('userMlp.maxValueError', {
        max: field.errors['max'].max,
      });
    }
    return '';
  }

  private shortenAddress(address: string, startChars = 8, endChars = 6): string {
    if (!address) return 'N/A';
    const minLength = startChars + endChars;
    if (address.length <= minLength) return address;
    return `${address.substring(0, startChars)}…${address.substring(address.length - endChars)}`;
  }
}
